import {Component, OnDestroy, OnInit} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ModalService } from "../../services/modal.service";
import { Subscription, take } from "rxjs";
import { UserDataService } from "../user-list/services/user-data.service";
import { UserInfoDto } from "../user-list/models/user-models";
import { fullNameValidator } from "../../validators/fullName.validator";
import { EditProfileDto } from "./models/profile.models";
import { ProfileDataService } from "./services/profile-data.service";
import { NotificationService } from "../../services/notification.service";
import { UserInfoService } from "../../services/user-info.service";
import { ActivatedRoute } from "@angular/router";
import { AuthService } from "src/app/services/auth/auth.service";

@Component({
	selector: "app-profile",
	templateUrl: "./profile.component.html",
	styleUrls: ["./profile.component.scss"],
})
export class ProfileComponent implements OnInit, OnDestroy {
	form!: FormGroup;
	isEdit = false;

	fistLetter = "";

	userData: UserInfoDto | undefined;

	isOpenModalChangePassword = false;

	subs: Subscription[] = [];

	constructor(
		private modalService: ModalService,
		private userDataService: UserDataService,
		private profileDataService: ProfileDataService,
		private notificationService: NotificationService,
		private userInfoService: UserInfoService,
		private activatedRoute: ActivatedRoute,
		private authService: AuthService
	) {}

	ngOnInit() {
		this.checkAuthStatus();
		this.initForm();
		this.loadDataUser();
		this.initModal();
	}

	checkAuthStatus() {
		this.subs.push(
			this.authService.isAuth$.pipe(take(1)).subscribe({
				next: (isAuth) => {
					if (!isAuth) {
						this.authService.logout();
					}
				},
			})
		);
	}

	ngOnDestroy() {
		this.subs.forEach((item) => item.unsubscribe());
	}

	loadDataUser() {
		const userId = localStorage.getItem("user_id");
		if (userId) {
			this.subs.push(
				this.userDataService.loadUserInfo(+userId).subscribe({
					next: (res) => {
						this.userData = res;
						this.form.patchValue({ fullNameFormControl: res.full_name });
						this.form.patchValue({ phoneFormControl: res.phone_number.substring(2) });
						this.form.patchValue({ roleFormControl: res.role_name });
						this.form.patchValue({ emailFormControl: res.email });
						this.fistLetter = res.full_name.substring(0, 1);
						this.userInfoService.userInfo$.next(res);
					},
				})
			);
		}
	}

	initModal() {
		this.subs.push(
			this.modalService.isModalOpen$.subscribe({
				next: (res) => {
					if (!res) {
						this.isOpenModalChangePassword = false;
					}
				},
			})
		);

		this.subs.push(
			this.activatedRoute.queryParams.subscribe((params) => {
				if (params["new-password"]) {
					setTimeout(() => {
						this.isOpenModalChangePassword = true;
					}, 500);
				}
			})
		);
	}

	initForm(): void {
		this.form = new FormGroup({
			fullNameFormControl: new FormControl("", [Validators.required, fullNameValidator()]),
			phoneFormControl: new FormControl("", [Validators.required, Validators.minLength(10)]),
			roleFormControl: new FormControl(""),
			emailFormControl: new FormControl(""),
		});
	}

	get fullNameRequiredError() {
		return this.form.get("fullNameFormControl")?.hasError("required");
	}

	get fullNameFormatError() {
		return this.form.get("fullNameFormControl")?.hasError("fullNameNotValid");
	}

	get phoneRequiredError() {
		return this.form.get("phoneFormControl")?.hasError("required");
	}

	get phoneFormatError() {
		return this.form.get("phoneFormControl")?.hasError("minlength");
	}

	sendForm() {
		const data: EditProfileDto = {
			full_name: this.form.get("fullNameFormControl")?.value,
			phone_number: `+7${this.form.get("phoneFormControl")?.value}`,
		};
		this.subs.push(
			this.profileDataService.editProfile(data).subscribe({
				next: () => {
					this.quitEdit();
					this.notificationService.setConfiguration(
						"icon-1",
						"warning",
						"PROFILE_PAGE.NOTIFICATION.EDIT_SUCCESS",
						"PROFILE_PAGE.NOTIFICATION.DESCRIPTION"
					);
					this.notificationService.showNotification();
				},
			})
		);
	}

	quitEdit() {
    this.isEdit = false;
    this.loadDataUser();
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.form.updateValueAndValidity();
	}

	change2FA(isEnabled: boolean) {
		this.subs.push(
			this.profileDataService.change2FA(isEnabled).subscribe({
				next: () => {
					this.loadDataUser();
					this.notificationService.setConfiguration(
						isEnabled ? "icon-8" : "icon-7",
						"warning",
						`PROFILE_PAGE.NOTIFICATION.${isEnabled ? "2FA_ON" : "2FA_OFF"}`,
						"PROFILE_PAGE.NOTIFICATION.DESCRIPTION"
					);
					this.notificationService.showNotification();
				},
			})
		);
	}

	openModalChangePassword() {
		this.isOpenModalChangePassword = true;
	}

	changePassword() {
		this.notificationService.setConfiguration(
			"icon-2",
			"warning",
			"PROFILE_PAGE.NOTIFICATION.PASSWORD_CHANGE_SUCCESS",
			"PROFILE_PAGE.NOTIFICATION.DESCRIPTION"
		);
		this.notificationService.showNotification();
	}
}
